// TanStack Query hooks pro station_group_type/source/member (statické, syncem generované skupiny)
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'

export function useStationGroupTypes() {
  const { data = [] } = useQuery({
    queryKey: ['stationGroupTypes'],
    queryFn: () => api.get('/station_group_type?sort=sort_order'),
    staleTime: Infinity,
  })
  return data
}

// Zdroje (šablony) daného typu — level 1..N, datasource NULL nebo 'postgres'/'oracle'
export function useStationGroupSources(typeId) {
  const { data = [] } = useQuery({
    queryKey: ['stationGroupSources', typeId],
    queryFn: () => api.get(`/station_group_source?type_id=${encodeURIComponent(typeId)}&sort=level`),
    enabled: !!typeId,
    staleTime: Infinity,
  })
  return data
}

// Vygenerované podskupiny daného zdroje (jedna úroveň hierarchie)
export function useStationGroupsBySource(sourceId) {
  const { data = [] } = useQuery({
    queryKey: ['stationGroupsBySource', sourceId],
    queryFn: () => api.get(`/station_group?source_id=${encodeURIComponent(sourceId)}&sort=code`),
    enabled: !!sourceId,
    staleTime: Infinity,
  })
  return data
}

// Ploché station_group řádky přímo pod daným station_group_type.code (bez procházení
// station_group_source hierarchie) — použito pro defaultní skupinu zjednodušené stránky
// /station-charts (station_group_type.id=14/code='simple_profi_type', viz
// useDefaultSimpleGroup.js), server vrací jen aktuální datasource větev uživatele.
export function useStationGroupsByTypeCode(typeCode, { withStatus = false } = {}) {
  const enabled = !!typeCode
  const q = useQuery({
    queryKey: ['stationGroupsByTypeCode', typeCode],
    queryFn: () => api.get(`/station_group?type_id=${encodeURIComponent(typeCode)}&sort=code`),
    enabled,
    staleTime: Infinity,
  })
  if (withStatus) return { data: q.data ?? [], isReady: !enabled || q.isSuccess }
  return q.data ?? []
}

// Vygenerované podskupiny napříč více zdroji najednou (server vrací jen aktuální datasource větev
// uživatele — proto se v definici skupiny ukládá station_group.code, ne id, a resolvuje se přes
// tohle vždy znovu na aktuální id, viz StationGroupLayout.jsx).
export function useStationGroupsBySources(sourceIds, { withStatus = false } = {}) {
  const key = [...new Set(sourceIds)].sort((a, b) => a - b).join(',')
  const enabled = sourceIds.length > 0
  const q = useQuery({
    queryKey: ['stationGroupsBySources', key],
    queryFn: () => api.get(`/station_group?source_id=[${key}]&sort=source_id,code`),
    enabled,
    staleTime: Infinity,
  })
  if (withStatus) return { data: q.data ?? [], isReady: !enabled || q.isSuccess }
  return q.data ?? []
}

// Členství (group_id -> gh_id) pro seznam group_id najednou
export function useStationGroupMembers(groupIds, { withStatus = false } = {}) {
  const key = [...groupIds].sort().join(',')
  const enabled = groupIds.length > 0
  const q = useQuery({
    queryKey: ['stationGroupMembers', key],
    queryFn: () => api.get(`/station_group_member?group_id=[${groupIds.join(',')}]`),
    enabled,
    staleTime: Infinity,
  })
  if (withStatus) return { data: q.data ?? [], isReady: !enabled || q.isSuccess }
  return q.data ?? []
}

// Uložené (pojmenované) skupiny, viz sekce VI plánu
export function useSavedStationGroups(enabled = true) {
  const { data = [] } = useQuery({
    queryKey: ['saved-station-groups'],
    queryFn: () => api.get('/saved_station_group?is_active=Y&sort=sort_order'),
    staleTime: 60_000,
    enabled,
  })
  return data
}

// Doplňkový filtr pro dvojici (uložená skupina, produkt), viz doc/plans/group-product-filters.md
export function useSavedStationGroupFilter(groupId, productCode) {
  const { data = [] } = useQuery({
    queryKey: ['saved-station-group-filter', groupId, productCode],
    queryFn: () => api.get(`/saved_station_group_filter?group_id=${encodeURIComponent(groupId)}&product_code=${encodeURIComponent(productCode)}`),
    enabled: !!groupId && !!productCode,
    staleTime: 60_000,
  })
  return data[0] ?? null
}

// Všechny doplňkové filtry uložené pro danou skupinu (napříč produkty), viz
// doc/plans/admin-station-groups-inline-edit.md bod 2 — použito v rozbalené sekci admin tabulky.
export function useSavedStationGroupFilters(groupId, enabled = true) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['saved-station-group-filters', groupId],
    queryFn: () => api.get(`/saved_station_group_filter?group_id=${encodeURIComponent(groupId)}`),
    enabled: !!groupId && enabled,
    staleTime: 60_000,
  })
  return { filters: data, isLoading }
}
